import type { SyncStorage } from 'jotai/vanilla/utils/atomWithStorage';

export function memoryBackend(): Storage {
    const map = new Map<string, string>();
    return {
        get length() {
            return map.size;
        },
        clear: () => map.clear(),
        getItem: k => (map.has(k) ? (map.get(k) as string) : null),
        key: i => Array.from(map.keys())[i] ?? null,
        removeItem: k => {
            map.delete(k);
        },
        setItem: (k, v) => {
            map.set(k, v);
        },
    };
}

let backend: Storage = typeof localStorage !== 'undefined' ? localStorage : memoryBackend();

/** Test-only: swap the underlying Storage backing all preference atoms and the migration.
 *  The house way to fake prefs: `__setPrefBackend(memoryBackend())` plus the REAL
 *  registry, not `vi.mock('@/preferences')`. A test that seeds nothing then reads
 *  the registry's defaults, and `store.set(prefs[key].atom, v)` on the test's own
 *  store flips a pref live. */
export function __setPrefBackend(s: Storage): void {
    backend = s;
}

/**
 * Live proxy to the current backend — reads are deferred to call time so swapping
 * `backend` retroactively affects all consumers (adapters + migration).
 */
export const prefStorage = {
    getItem: (key: string) => backend.getItem(key),
    setItem: (key: string, value: string) => backend.setItem(key, value),
    removeItem: (key: string) => backend.removeItem(key),
};

/**
 * Synchronous raw string storage for atomWithStorage.
 * Reads/writes plain strings without JSON serialization,
 * matching the existing Redux localStorage format.
 */
export const rawStringStorage: SyncStorage<string> = {
    getItem: key => backend.getItem(key) ?? '',
    setItem: (key, value) => backend.setItem(key, value),
    removeItem: key => backend.removeItem(key),
};

/**
 * Synchronous numeric storage for atomWithStorage.
 * Reads/writes numbers stored as plain strings (e.g. "42"),
 * matching the existing Redux localStorage format.
 */
export const rawNumberStorage: SyncStorage<number | null> = {
    getItem: key => {
        const value = backend.getItem(key);
        if (value === null || value === '' || value === 'null') return null;
        const num = Number(value);
        return Number.isNaN(num) ? null : num;
    },
    setItem: (key, value) => {
        if (value === null) {
            backend.removeItem(key);
        } else {
            backend.setItem(key, value.toString());
        }
    },
    removeItem: key => backend.removeItem(key),
};

/**
 * Synchronous boolean storage for atomWithStorage.
 * Reads/writes booleans as raw "true"/"false" strings,
 * matching the existing editor localStorage format.
 */
export const rawBoolStorage = (defaultValue: boolean): SyncStorage<boolean> => ({
    getItem: key => {
        const value = backend.getItem(key);
        if (value === null) return defaultValue;
        return value === 'true';
    },
    setItem: (key, value) => backend.setItem(key, String(value)),
    removeItem: key => backend.removeItem(key),
});

/**
 * Synchronous JSON storage for atomWithStorage.
 * For values that were already stored as JSON (e.g. objects/arrays).
 */
export const syncJsonStorage = <T>(): SyncStorage<T> => ({
    getItem: (key, initialValue) => {
        const value = backend.getItem(key);
        if (value === null) return initialValue;
        try {
            return JSON.parse(value) as T;
        } catch {
            return initialValue;
        }
    },
    setItem: (key, value) => backend.setItem(key, JSON.stringify(value)),
    removeItem: key => backend.removeItem(key),
});
